import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Card, 
  Button, 
  Tooltip, 
  Space, 
  Badge, 
  Select,
  Divider,
  Typography,
  Alert,
  Popconfirm,
  Switch,
  Radio,
  Collapse
} from 'antd';
import { 
  EditOutlined,
  ClearOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  LineOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

// 🎯 MOBILE DEBUG: Simple console widget for mobile debugging
const MobileConsole = ({ logs }) => {
  const [isVisible, setIsVisible] = useState(window.innerWidth <= 768);
  
  if (!isVisible || window.innerWidth > 768) return null;
  
  return (
    <div style={{
      position: 'fixed',
      bottom: '10px',
      left: '10px',
      right: '10px',
      maxHeight: '200px',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      color: 'white',
      padding: '10px',
      fontSize: '12px',
      fontFamily: 'monospace',
      overflowY: 'auto',
      zIndex: 10000,
      border: '1px solid #ccc',
      borderRadius: '5px'
    }}>
      <div style={{ marginBottom: '5px', fontWeight: 'bold' }}>
        🎯 MOBILE DEBUG CONSOLE
        <button 
          onClick={() => setIsVisible(false)}
          style={{ float: 'right', background: 'red', color: 'white', border: 'none', padding: '2px 5px' }}
        >
          ✕
        </button>
      </div>
      {logs.slice(-10).map((log, i) => (
        <div key={i} style={{ marginBottom: '2px', wordBreak: 'break-word' }}>
          {log}
        </div>
      ))}
    </div>
  );
};

/**
 * AdvancedDrawingTools - Enhanced drawing tools with editing, persistence, and multi-shape support
 */
const AdvancedDrawingTools = ({
  map,
  onShapeComplete,
  onSelectionChange,
  className = '',
  // Measurement tool props (from MapCore)
  measurementMode = 'idle',
  onStartMeasurement = null,
  onClearMeasurement = null,
  // Phase 2B-Alpha: Measurement functions ref for circle integration
  measurementFunctionsRef = null,
  // Phase C-Alpha: Unit preference props
  unitPreference = 'km',
  onUnitPreferenceChange = null,
  // Phase 2C-Alpha: Ring styling props
  ringStyleConfig = null,
  onRingStyleChange = null,
  // PHASE 1: New prop for circle site detection
  onCircleComplete = null
}) => {
  // 🎯 MOBILE DEBUG: Console logs state
  const [mobileLogs, setMobileLogs] = useState([]);
  
  const addMobileLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setMobileLogs(prev => [...prev, `${timestamp}: ${message}`]);
    console.log(message); // Also log to regular console
  };
  console.log('🎯 AdvancedDrawingTools props:', {
    measurementMode,
    onStartMeasurement: !!onStartMeasurement,
    onClearMeasurement: !!onClearMeasurement,
    map: !!map
  });
  const [drawingManager, setDrawingManager] = useState(null);
  const [currentTool, setCurrentTool] = useState(null);
  const [shapes, setShapes] = useState([]);
  const [selectedShape, setSelectedShape] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const shapeIdCounter = useRef(0);
  const onShapeCompleteRef = useRef(onShapeComplete);

  // Phase 2A: Radius rings are now handled by MapCore
  
  // Phase 2B-Beta: Reactive measurements state for UI updates
  const [measurements, setMeasurements] = useState([]);
  
  // Accordion state - default collapsed
  const [activePanel, setActivePanel] = useState([]);

  useEffect(() => {
    onShapeCompleteRef.current = onShapeComplete;
  }, [onShapeComplete]);



  // Drawing tool configurations
  const DRAWING_TOOLS = {
    circle: {
      icon: <EditOutlined />,
      title: 'Draw Circle',
      mode: 'circle'
    },
    measure: {
      icon: <LineOutlined />,
      title: 'Measure Distance',
      mode: 'measure'
    }
  };

  // Selection modes - REMOVED FOR PHASE 1
  // const SELECTION_MODES = {
  //   replace: { label: 'Replace Selection', color: '#39325b' },
  //   add: { label: 'Add to Selection', color: '#27ae60' },
  //   subtract: { label: 'Remove from Selection', color: '#e74c3c' }
  // };

  // Phase C-Alpha: Unit-aware distance formatting helper
  const formatDistanceForUnit = (distanceMeters, distanceKm, distanceMiles) => {
    if (unitPreference === 'mi') {
      return distanceMiles ? `${distanceMiles} mi` : `${(distanceMeters / 1609.34).toFixed(2)} mi`;
    } else {
      return distanceKm ? `${distanceKm} km` : `${(distanceMeters / 1000).toFixed(2)} km`;
    }
  };

  // Helper functions (no useCallback - prevent recreation issues)
  const getShapeBounds = (shape, type) => {
    let bounds;

    switch (type) {
      case 'circle':
        const center = shape.getCenter();
        const radius = shape.getRadius();
        bounds = new window.google.maps.LatLngBounds();
        // Approximate circle bounds
        const lat = center.lat();
        const lng = center.lng();
        const latOffset = radius / 111320; // Rough conversion
        const lngOffset = radius / (111320 * Math.cos(lat * Math.PI / 180));
        bounds.extend({ lat: lat + latOffset, lng: lng + lngOffset });
        bounds.extend({ lat: lat - latOffset, lng: lng - lngOffset });
        break;
      default:
        return null;
    }

    return bounds;
  };

  // Phase 2C-Alpha: Get current circle style for drawing manager
  const getCurrentCircleStyle = () => {
    if (!ringStyleConfig) {
      return {
        fillColor: '#39325b',
        fillOpacity: 0.2,
        strokeColor: '#39325b',
        strokeOpacity: 0.8,
        strokeWeight: 2
      };
    }

    // Color schemes for drawing manager (matches MapCore)
    const COLOR_SCHEMES = {
      red: '#ff0000',
      blue: '#0066cc', 
      green: '#00cc66',
      purple: '#9966cc',
      orange: '#fa8c16'
    };

    const color = COLOR_SCHEMES[ringStyleConfig.colorScheme] || '#ff0000';
    
    return {
      fillColor: color,
      fillOpacity: ringStyleConfig.opacity?.fill || 0.1,
      strokeColor: color, 
      strokeOpacity: ringStyleConfig.opacity?.primary || 0.8,
      strokeWeight: ringStyleConfig.strokeWeight?.primary || 2
    };
  };

  // Initialize drawing manager ONLY ONCE - no dependencies on changing values
  useEffect(() => {
    if (map && window.google && !drawingManager) {
      const circleStyle = getCurrentCircleStyle();
      
      addMobileLog('🎯 MOBILE DEBUG: Creating Drawing Manager with map: ' + !!map);
      addMobileLog('🎯 MOBILE DEBUG: Map type: ' + map.constructor.name);
      addMobileLog('🎯 MOBILE DEBUG: Map gestureHandling: ' + map.get('gestureHandling'));
      
      const manager = new window.google.maps.drawing.DrawingManager({
        drawingMode: null,
        drawingControl: false,
        circleOptions: {
          fillColor: circleStyle.fillColor,
          fillOpacity: circleStyle.fillOpacity,
          strokeColor: circleStyle.strokeColor,
          strokeOpacity: circleStyle.strokeOpacity,
          strokeWeight: circleStyle.strokeWeight,
          clickable: true,
          editable: false,
          draggable: false
        }
      });
      
      addMobileLog('🎯 MOBILE DEBUG: Drawing Manager created: ' + !!manager);

      manager.setMap(map);
      
              // 🚀 GPT'S SIMPLE SOLUTION: Direct touch listeners for mobile circle drawing
        if (map && window.innerWidth <= 768) {
          const mapContainer = map.getDiv();
          if (mapContainer) {
            let centerLatLng = null;
            let isDrawingCircle = false;
            let tempCircle = null;
            
            const handleTouchStart = (e) => {
              if (currentTool === 'circle' || window.mobileDrawingMode === 'circle') {
                addMobileLog('🚀 GPT SOLUTION: TouchStart - Circle mode detected');
                e.preventDefault();
                e.stopPropagation();
                
                const touch = e.touches[0];
                const rect = mapContainer.getBoundingClientRect();
                
                // 🚀 GPT's simple coordinate conversion using map projection
                const projection = map.getProjection();
                if (projection) {
                  const topRight = projection.fromLatLngToPoint(map.getBounds().getNorthEast());
                  const bottomLeft = projection.fromLatLngToPoint(map.getBounds().getSouthWest());
                  
                  const worldPoint = new window.google.maps.Point(
                    bottomLeft.x + (touch.clientX - rect.left) * (topRight.x - bottomLeft.x) / rect.width,
                    topRight.y + (touch.clientY - rect.top) * (bottomLeft.y - topRight.y) / rect.height
                  );
                  
                  centerLatLng = projection.fromPointToLatLng(worldPoint);
                  isDrawingCircle = true;
                  
                  addMobileLog('🚀 GPT SOLUTION: Center set at ' + centerLatLng.lat() + ',' + centerLatLng.lng());
                }
              }
            };
            
            const handleTouchMove = (e) => {
              if (isDrawingCircle && centerLatLng && (currentTool === 'circle' || window.mobileDrawingMode === 'circle')) {
                e.preventDefault();
                e.stopPropagation();
                
                const touch = e.touches[0];
                const rect = mapContainer.getBoundingClientRect();
                
                // Same coordinate conversion as touchStart
                const projection = map.getProjection();
                if (projection) {
                  const topRight = projection.fromLatLngToPoint(map.getBounds().getNorthEast());
                  const bottomLeft = projection.fromLatLngToPoint(map.getBounds().getSouthWest());
                  
                  const worldPoint = new window.google.maps.Point(
                    bottomLeft.x + (touch.clientX - rect.left) * (topRight.x - bottomLeft.x) / rect.width,
                    topRight.y + (touch.clientY - rect.top) * (bottomLeft.y - topRight.y) / rect.height
                  );
                  
                  const currentLatLng = projection.fromPointToLatLng(worldPoint);
                  const radius = window.google.maps.geometry.spherical.computeDistanceBetween(centerLatLng, currentLatLng);
                  
                  // Remove previous temp circle
                  if (tempCircle) {
                    tempCircle.setMap(null);
                  }
                  
                  // Create preview circle (GPT style)
                  tempCircle = new window.google.maps.Circle({
                    center: centerLatLng,
                    radius: radius,
                    strokeColor: '#00FF00',
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    fillColor: '#00FF00',
                    fillOpacity: 0.2,
                    map: map
                  });
                  
                  addMobileLog('🚀 GPT SOLUTION: Preview radius ' + Math.round(radius) + 'm');
                }
              }
            };
            
            const handleTouchEnd = (e) => {
              if (isDrawingCircle && centerLatLng && (currentTool === 'circle' || window.mobileDrawingMode === 'circle')) {
                e.preventDefault();
                e.stopPropagation();
                
                isDrawingCircle = false;
                
                const touch = e.changedTouches[0];
                const rect = mapContainer.getBoundingClientRect();
                
                // Final coordinate conversion
                const projection = map.getProjection();
                if (projection) {
                  const topRight = projection.fromLatLngToPoint(map.getBounds().getNorthEast());
                  const bottomLeft = projection.fromLatLngToPoint(map.getBounds().getSouthWest());
                  
                  const worldPoint = new window.google.maps.Point(
                    bottomLeft.x + (touch.clientX - rect.left) * (topRight.x - bottomLeft.x) / rect.width,
                    topRight.y + (touch.clientY - rect.top) * (bottomLeft.y - topRight.y) / rect.height
                  );
                  
                  const endLatLng = projection.fromPointToLatLng(worldPoint);
                  const finalRadius = window.google.maps.geometry.spherical.computeDistanceBetween(centerLatLng, endLatLng);
                  
                  // Remove temp circle
                  if (tempCircle) {
                    tempCircle.setMap(null);
                    tempCircle = null;
                  }
                  
                  // Create final circle
                  const finalCircle = new window.google.maps.Circle({
                    center: centerLatLng,
                    radius: finalRadius,
                    strokeColor: '#FF0000',
                    strokeOpacity: 1.0,
                    strokeWeight: 3,
                    fillColor: '#FF0000',
                    fillOpacity: 0.35,
                    map: map
                  });
                  
                  addMobileLog('🚀 GPT SOLUTION: Final circle created with radius ' + Math.round(finalRadius) + 'm');
                  
                  // Trigger completion callback
                  if (onCircleComplete) {
                    onCircleComplete(finalCircle);
                  }
                  
                  // Reset state
                  setCurrentTool(null);
                  window.mobileDrawingMode = null;
                  map.setOptions({ gestureHandling: 'greedy' });
                  centerLatLng = null;
                }
              }
            };
            
            // Attach GPT's simple touch listeners
            addMobileLog('🚀 GPT SOLUTION: Attaching direct touch listeners');
            mapContainer.addEventListener('touchstart', handleTouchStart, { passive: false });
            mapContainer.addEventListener('touchmove', handleTouchMove, { passive: false });
            mapContainer.addEventListener('touchend', handleTouchEnd, { passive: false });
          }
        }
      
      setDrawingManager(manager);

      // 🎯 MOBILE DEBUG: Add listeners to detect drawing attempts
      window.google.maps.event.addListener(manager, 'drawingmode_changed', () => {
        addMobileLog('🎯 MOBILE DEBUG: Drawing mode changed to: ' + manager.getDrawingMode());
      });
      
      // 🎯 MOBILE DEBUG: Listen for when user starts drawing
      if (window.google.maps.event.addListener) {
        try {
          // This might not exist, but worth trying
          window.google.maps.event.addListener(manager, 'circlecomplete', (event) => {
            console.log('🎯 MOBILE DEBUG: Circle complete event fired!', event);
          });
        } catch (e) {
          console.log('🎯 MOBILE DEBUG: circlecomplete event not available');
        }
      }

      // Handle shape completion with inline function to avoid dependencies
      window.google.maps.event.addListener(manager, 'overlaycomplete', (event) => {
        const shape = event.overlay;
        const shapeType = event.type;
        const shapeId = `shape_${++shapeIdCounter.current}`;

        // PHASE 1: Simplified shape data without selection mode
        const shapeData = {
          id: shapeId,
          type: shapeType,
          shape: shape,
          bounds: getShapeBounds(shape, shapeType),
          visible: true,
          created: new Date().toISOString()
        };

        setShapes(prev => [...prev, shapeData]);

        // Phase 2A: Create radius rings for circles
        if (shapeType === 'circle') {
          // Phase 2B-Alpha: Add circle to measurements array
          // Radius rings are now handled by MapCore to avoid duplication
          addCircleToMeasurements(shape, shapeData);
          
          // PHASE 1: Trigger site detection for circles
          if (onCircleComplete) {
            console.log('🔬 AdvancedDrawingTools: Circle completed - triggering site detection');
            console.log('🔬 AdvancedDrawingTools: onCircleComplete function available:', typeof onCircleComplete);
            console.log('🔬 AdvancedDrawingTools: Circle shape:', shape);
            onCircleComplete(shape);
            console.log('🔬 AdvancedDrawingTools: Site detection callback completed');
          } else {
            console.log('❌ AdvancedDrawingTools: onCircleComplete callback not available');
          }
        }

        // PHASE 1: Simplified click listeners without selection mode colors
        window.google.maps.event.addListener(shape, 'click', () => {
          setSelectedShape(prevSelected => {
            if (prevSelected) {
              // Use default color since SELECTION_MODES is removed for Phase 1
              prevSelected.shape.setOptions({
                strokeColor: '#39325b',
                strokeWeight: 2,
                strokeOpacity: 0.8
              });
            }
            
            // Highlight selected shape
            shape.setOptions({
              strokeColor: '#000',
              strokeWeight: 3,
              strokeOpacity: 1
            });
            
            return shapeData;
          });
        });

        // Double-click to edit
        window.google.maps.event.addListener(shape, 'dblclick', () => {
          setIsEditing(true);
          shapeData.shape.setEditable(true);
          shapeData.shape.setDraggable(true);
        });

        // Stop drawing
        if (manager) {
          manager.setDrawingMode(null);
        }
        
        // 🎯 CRITICAL FIX: Restore greedy gesture handling after drawing
        if (map) {
          addMobileLog('🎯 MOBILE DRAWING: Restoring gestureHandling to greedy after drawing');
          map.setOptions({ gestureHandling: 'greedy' });
        }
        
        setCurrentTool(null);

        // PHASE 1: Keep legacy callback for backwards compatibility (bounds only)
        if (onShapeCompleteRef.current) {
          onShapeCompleteRef.current(shapeData.bounds, 'replace');
        }
      });

      return () => {
        if (manager) {
          manager.setMap(null);
        }
      };
    }
  }, [map]); // Only depends on map

  // Phase 2C-Alpha: Update drawing manager options when ring style changes
  useEffect(() => {
    if (drawingManager) {
      // Phase 2C-Alpha: Use ring style for circles
      const circleStyle = getCurrentCircleStyle();
      const circleOptions = {
        fillColor: circleStyle.fillColor,
        fillOpacity: circleStyle.fillOpacity,
        strokeColor: circleStyle.strokeColor,
        strokeOpacity: circleStyle.strokeOpacity,
        strokeWeight: circleStyle.strokeWeight,
        clickable: true,
        editable: false,
        draggable: false
      };
      
      drawingManager.setOptions({
        circleOptions: circleOptions
      });
      
      console.log('🎨 Updated drawing manager options - Circle style:', circleOptions);
    }
  }, [drawingManager, ringStyleConfig]);

  // 🚨 TEMPORARILY DISABLED: This sync effect was causing circles to be hidden immediately after creation
  // The toggle functions should handle visibility changes instead
  /*
  // Phase C-Alpha: Sync circle shape visibility with measurements array
  useEffect(() => {
    if (!measurementFunctionsRef?.current?.measurements) return;

    const measurements = measurementFunctionsRef.current.measurements;
    const circleMeasurements = measurements.filter(measurement => measurement.type === 'circle');
    
    // Find circle measurements and sync their visibility with shapes
    circleMeasurements.forEach(measurement => {
      const shapeData = shapes.find(shape => shape.id === measurement.id);
      if (shapeData && shapeData.visible !== measurement.visible) {
        console.log('🔄 Phase C-Alpha: Syncing circle visibility from measurements:', measurement.id, measurement.visible);
        
        // Update the actual Google Maps shape visibility using setMap for consistency
        shapeData.shape.setMap(measurement.visible ? map : null);
        shapeData.visible = measurement.visible;
        
        // Update shapes array to trigger re-render
        setShapes(prev => prev.map(shape => 
          shape.id === measurement.id ? { ...shape, visible: measurement.visible } : shape
        ));
      }
    });

    // Phase C-Alpha: Detect deleted circle measurements and remove corresponding shapes
    const existingCircleMeasurementIds = new Set(circleMeasurements.map(m => m.id));
    const circleShapes = shapes.filter(shape => shape.type === 'circle');
    
    circleShapes.forEach(shapeData => {
      if (!existingCircleMeasurementIds.has(shapeData.id)) {
        console.log('🗑️ Phase C-Alpha: Circle measurement deleted, removing shape:', shapeData.id);
        
        // Remove the Google Maps shape object
        shapeData.shape.setMap(null);
        
        // Remove from shapes array
        setShapes(prev => prev.filter(shape => shape.id !== shapeData.id));
        
        // If this was the selected shape, clear selection
        if (selectedShape?.id === shapeData.id) {
          setSelectedShape(null);
        }
      }
    });
  }, [measurementFunctionsRef?.current?.measurements, shapes]);
  */



  // Phase 2B-Beta: Sync measurements from MapCore for reactive UI
  useEffect(() => {
    // Use an interval to check for changes in measurements
    const syncMeasurements = () => {
      if (measurementFunctionsRef?.current?.measurements) {
        const currentMeasurements = measurementFunctionsRef.current.measurements;
        setMeasurements(prev => {
          // Only update if the array actually changed
          if (JSON.stringify(prev) !== JSON.stringify(currentMeasurements)) {
            console.log('🔄 Syncing measurements from MapCore:', currentMeasurements);
            return [...currentMeasurements];
          }
          return prev;
        });
      }
    };

    // Initial sync
    syncMeasurements();

    // Set up interval to check for changes
    const interval = setInterval(syncMeasurements, 100);

    return () => clearInterval(interval);
  }, [measurementFunctionsRef]);

  // Simple functions - no useCallback to avoid dependency issues
  const startDrawing = (tool) => {
    addMobileLog('🎯 startDrawing called with tool: ' + tool);
    if (!drawingManager) {
      addMobileLog('🎯 ERROR: No drawing manager available!');
      return;
    }

    // Handle measurement tool separately
    if (tool === 'measure') {
      console.log('🎯 Measurement tool detected, calling startMeasurement');
      startMeasurement();
      return;
    }

    // 🎯 CRITICAL FIX: Disable map gestures completely during drawing on mobile
    if (map) {
      addMobileLog('🎯 MOBILE DRAWING: Setting gestureHandling to none for drawing');
      map.setOptions({ gestureHandling: 'none' });
    }

          // 🎯 MOBILE FIX: Small delay to ensure gesture handling change takes effect
      setTimeout(() => {
    const toolMode = window.google.maps.drawing.OverlayType[tool.toUpperCase()];
        addMobileLog('🎯 MOBILE DRAWING: Activating drawing mode for: ' + tool);
        addMobileLog('🎯 MOBILE DEBUG: Tool mode value: ' + toolMode);
        addMobileLog('🎯 MOBILE DEBUG: Drawing manager exists: ' + !!drawingManager);
        addMobileLog('🎯 MOBILE DEBUG: Map gestureHandling before draw: ' + map?.get('gestureHandling'));
        
    drawingManager.setDrawingMode(toolMode);
        
        // 🎯 MOBILE DEBUG: Set our own tracking
        if (window.innerWidth <= 768) {
          window.mobileDrawingMode = tool;
          addMobileLog('🎯 MOBILE DEBUG: Set mobile drawing mode to: ' + tool);
          addMobileLog('🎯 MOBILE DEBUG: Window width: ' + window.innerWidth);
          addMobileLog('🎯 MOBILE DEBUG: Is mobile: ' + (window.innerWidth <= 768));
        } else {
          addMobileLog('🎯 MOBILE DEBUG: Not mobile, width: ' + window.innerWidth);
        }
        
        addMobileLog('🎯 MOBILE DEBUG: Drawing mode set, current mode: ' + drawingManager.getDrawingMode());
        
    setCurrentTool(tool);
      }, 50);
  };

  // Measurement tool functions (using callbacks to MapCore)
  const startMeasurement = () => {
    console.log('🎯 AdvancedDrawingTools startMeasurement called');
    console.log('🎯 onStartMeasurement function:', onStartMeasurement);
    setCurrentTool('measure');
    if (onStartMeasurement) {
      console.log('🎯 Calling onStartMeasurement callback');
      onStartMeasurement();
    } else {
      console.log('🎯 ERROR: onStartMeasurement is null/undefined');
    }
  };

  const clearMeasurement = () => {
    if (currentTool === 'measure') {
      setCurrentTool(null);
      
      // 🎯 CRITICAL FIX: Restore greedy gesture handling after clearing measurement
      if (map) {
        addMobileLog('🎯 MOBILE DRAWING: Restoring gestureHandling to greedy after clearing measurement');
        map.setOptions({ gestureHandling: 'greedy' });
      }
    }
    if (onClearMeasurement) {
      onClearMeasurement();
    }
  };

  // 🎯 CRITICAL FIX: Utility function to stop drawing and restore gesture handling
  const stopDrawing = () => {
    addMobileLog('🎯 MOBILE DRAWING: Stopping drawing and restoring gesture handling');
    
    if (drawingManager) {
      drawingManager.setDrawingMode(null);
    }
    
    if (map) {
      addMobileLog('🎯 MOBILE DRAWING: Restoring gestureHandling to greedy');
      map.setOptions({ gestureHandling: 'greedy' });
    }
    
    setCurrentTool(null);
  };

  const stopEditingShape = () => {
    setSelectedShape(prevSelected => {
      if (prevSelected) {
        prevSelected.shape.setEditable(false);
        prevSelected.shape.setDraggable(false);
      }
      return prevSelected;
    });
    setIsEditing(false);
  };

  const deleteShape = (shapeData) => {
    if (!shapeData) {
      // If no shape provided, delete currently selected shape
          setSelectedShape(prevSelected => {
      if (prevSelected) {
        // Phase 2B-Alpha: Remove from measurements array if it's a circle
        if (prevSelected.type === 'circle') {
          removeCircleFromMeasurements(prevSelected.id);
        }
        
        prevSelected.shape.setMap(null);
        setShapes(prev => prev.filter(s => s.id !== prevSelected.id));
      }
      return null;
    });
      return;
    }

    // Phase 2B-Alpha: Remove from measurements array if it's a circle
    if (shapeData.type === 'circle') {
      removeCircleFromMeasurements(shapeData.id);
    }

    // Delete specific shape
    shapeData.shape.setMap(null);
    setShapes(prev => prev.filter(s => s.id !== shapeData.id));
    
    // Clear selection if deleting selected shape
    setSelectedShape(prevSelected => 
      prevSelected?.id === shapeData.id ? null : prevSelected
    );
  };

  const toggleShapeVisibility = (shapeData) => {
    const newVisibility = !shapeData.visible;
    shapeData.shape.setMap(newVisibility ? map : null);
    shapeData.visible = newVisibility;

    // Phase 2B-Alpha: Update visibility in measurements array for circles
    if (shapeData.type === 'circle') {
      updateCircleMeasurementVisibility(shapeData.id, newVisibility);
    }
  };

  const clearAllShapes = () => {
    shapes.forEach(shapeData => {
      // Phase 2B-Alpha: Remove from measurements array for circles
      if (shapeData.type === 'circle') {
        removeCircleFromMeasurements(shapeData.id);
      }
      
      shapeData.shape.setMap(null);
    });
    setShapes([]);
    setSelectedShape(null);
  };



  const startEditingShape = (shapeData) => {
    if (!shapeData) return;
    
    setIsEditing(true);
    shapeData.shape.setEditable(true);
    shapeData.shape.setDraggable(true);
  };

  const selectShape = (shapeData) => {
    setSelectedShape(prevSelected => {
      // Restore previous shape appearance
      if (prevSelected) {
        // Use default color since SELECTION_MODES is removed for Phase 1
        prevSelected.shape.setOptions({
          strokeColor: '#39325b',
          strokeWeight: 2,
          strokeOpacity: 0.8
        });
      }
      
      // Highlight new selected shape
      if (shapeData) {
        shapeData.shape.setOptions({
          strokeColor: '#000',
          strokeWeight: 3,
          strokeOpacity: 1
        });
      }
      
      return shapeData;
    });
  };

  // Phase 2A-Beta: Smart distance rounding algorithm
  const calculateSmartRingDistances = (maxRadiusMeters) => {
    const maxKm = maxRadiusMeters / 1000;
    
    // Smart rounding logic based on circle size
    const getRoundedDistances = (maxDistance) => {
      if (maxDistance <= 0.1) {
        // Very small circles: 25m, 50m, 75m, 100m
        return [25, 50, 75, Math.round(maxDistance * 1000)].filter(d => d <= maxDistance * 1000);
      } else if (maxDistance <= 0.5) {
        // Small circles: round to nearest 50m
        const step = 0.05; // 50m steps
        return [1, 2, 3, 4].map(i => Math.round((maxDistance * i / 4) / step) * step * 1000)
                            .filter(d => d <= maxDistance * 1000);
      } else if (maxDistance <= 2) {
        // Medium circles: round to nearest 100m
        const step = 0.1; // 100m steps
        return [1, 2, 3, 4].map(i => Math.round((maxDistance * i / 4) / step) * step * 1000)
                            .filter(d => d <= maxDistance * 1000);
      } else if (maxDistance <= 10) {
        // Large circles: round to nearest 500m
        const step = 0.5; // 500m steps
        return [1, 2, 3, 4].map(i => Math.round((maxDistance * i / 4) / step) * step * 1000)
                            .filter(d => d <= maxDistance * 1000);
      } else if (maxDistance <= 50) {
        // Very large circles: round to nearest 1km
        const step = 1; // 1km steps
        return [1, 2, 3, 4].map(i => Math.round((maxDistance * i / 4) / step) * step * 1000)
                            .filter(d => d <= maxDistance * 1000);
      } else {
        // Huge circles: round to nearest 5km
        const step = 5; // 5km steps
        return [1, 2, 3, 4].map(i => Math.round((maxDistance * i / 4) / step) * step * 1000)
                            .filter(d => d <= maxDistance * 1000);
      }
    };

    return getRoundedDistances(maxKm);
  };

  // Phase 2A-Beta: Format distance for display
  const formatDistance = (meters) => {
    if (meters < 1000) {
      return `${Math.round(meters)}m`;
    } else {
      const km = meters / 1000;
      if (km < 10) {
        return `${km.toFixed(1)}km`;
      } else {
        return `${Math.round(km)}km`;
      }
    }
  };

  // Phase 2A: Radius ring functions removed - now handled by MapCore

  // Phase 2B-Alpha: Add circle to measurements array for unified management
  const addCircleToMeasurements = (circle, shapeData) => {
    if (!circle || !shapeData || !window.google) return;

    const center = circle.getCenter();
    const radiusMeters = circle.getRadius();
    
    // Calculate smart ring distances (same as radius rings)
    const ringDistances = calculateSmartRingDistances(radiusMeters);
    
    const circleMeasurement = {
      id: shapeData.id, // Use same ID as shape for synchronization
      label: `Circle ${shapeData.id}`,
      type: 'circle',
      center: {
        lat: center.lat(),
        lng: center.lng()
      },
      radius: radiusMeters,
      radiusKm: (radiusMeters / 1000).toFixed(2),
      radiusRings: ringDistances, // Array of distances in meters for MapCore to render
      ringDistances: ringDistances.map(distance => ({
        meters: distance,
        km: formatDistance(distance),
        label: formatDistance(distance)
      })),
      timestamp: shapeData.created,
      visible: shapeData.visible
    };

    console.log('🔄 Phase 2B-Alpha: Adding circle to measurements:', circleMeasurement);
    
    // 🔥 CRITICAL FIX: Remove the original circle from map before MapCore takes over
    // This prevents duplicate circles (original + MapCore's managed version)
    console.log('🔥 CIRCLE FIX: Removing original AdvancedDrawingTools circle from map');
    circle.setMap(null);
    console.log('🔥 CIRCLE FIX: Original circle removed, MapCore will create managed version');
    
    // Add to measurements via callback to MapCore
    if (measurementFunctionsRef && measurementFunctionsRef.current && measurementFunctionsRef.current.addCircleMeasurement) {
      measurementFunctionsRef.current.addCircleMeasurement(circleMeasurement);
    }
  };

  // Phase 2B-Alpha: Remove circle from measurements array
  const removeCircleFromMeasurements = (circleId) => {
    console.log('🔄 Phase 2B-Alpha: Removing circle from measurements:', circleId);
    
    if (measurementFunctionsRef && measurementFunctionsRef.current && measurementFunctionsRef.current.removeCircleMeasurement) {
      measurementFunctionsRef.current.removeCircleMeasurement(circleId);
    }
  };

  // Phase 2B-Alpha: Update circle visibility in measurements
  const updateCircleMeasurementVisibility = (circleId, visible) => {
    console.log('🔄 Phase 2B-Alpha: Updating circle visibility in measurements:', circleId, visible);
    
    if (measurementFunctionsRef && measurementFunctionsRef.current && measurementFunctionsRef.current.updateCircleMeasurementVisibility) {
      measurementFunctionsRef.current.updateCircleMeasurementVisibility(circleId, visible);
    }
  };

  const handleClearAllMeasurements = useCallback(() => {
    console.log('🔥 CLEAR ALL BUTTON CLICKED - AGGRESSIVE DEBUG');
    console.log('🔥 Map object:', !!map);
    console.log('🔥 measurementFunctionsRef:', !!map?.measurementFunctionsRef);
    console.log('🔥 measurementFunctionsRef.current:', !!map?.measurementFunctionsRef?.current);
    console.log('🔥 Available functions:', Object.keys(map?.measurementFunctionsRef?.current || {}));
    console.log('🔥 clearAllMeasurements function:', typeof map?.measurementFunctionsRef?.current?.clearAllMeasurements);
    console.log('🔥 Current measurements:', map?.measurementFunctionsRef?.current?.measurements || []);

    if (map?.measurementFunctionsRef?.current?.clearAllMeasurements) {
      console.log('🔥 Calling clearAllMeasurements...');
      map.measurementFunctionsRef.current.clearAllMeasurements();
      console.log('🔥 clearAllMeasurements called');
      
      // Also clear all circle shapes from the shapes array
      console.log('🔥 Current shapes before clearing:', shapes);
      const circleShapes = shapes.filter(shape => shape.type === 'circle');
      console.log('🔥 Circle shapes to delete:', circleShapes);
      
      circleShapes.forEach(shape => {
        console.log('🔥 Deleting circle shape:', shape.id);
        deleteShape(shape);
      });
      console.log('🔥 All circle shapes deleted');
    } else {
      console.error('🔥 clearAllMeasurements function not available!');
    }
  }, [map, shapes, deleteShape]);

  return (
    <div className={className}>
      <Collapse
        activeKey={activePanel}
        onChange={setActivePanel}
      size="small"
        style={{ 
          borderRadius: '8px',
          border: '2px solid #39325b',
          backgroundColor: '#fff'
        }}
      >
        <Panel header={<span style={{ fontWeight: 'bold' }}>Drawing and Measurement Tools</span>} key="1" style={{ borderRadius: '8px' }}>
      {/* Unit Preference Toggle */}
      {unitPreference && onUnitPreferenceChange && (
        <>
          <div className="mb-3">
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Units:</Text>
            <Radio.Group
              value={unitPreference}
              onChange={(e) => onUnitPreferenceChange(e.target.value)}
              size="small"
            >
              <Radio.Button value="km">Kilometers</Radio.Button>
              <Radio.Button value="mi">Miles</Radio.Button>
            </Radio.Group>
          </div>
          <Divider style={{ margin: '12px 0' }} />
        </>
      )}

      {/* Phase 2C-Alpha: Ring Styling Controls */}
      {ringStyleConfig && onRingStyleChange && (
        <>
          <div className="mb-3">
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Ring Color:</Text>
            <div style={{ marginBottom: 8 }}>
              <Select
                value={ringStyleConfig.colorScheme}
                onChange={(value) => onRingStyleChange({ ...ringStyleConfig, colorScheme: value })}
                style={{ width: '100%', marginTop: 4 }}
                size="small"
              >
                <Option value="red">🔴 Red</Option>
                <Option value="blue">🔵 Blue</Option>
                <Option value="green">🟢 Green</Option>
                <Option value="purple">🟣 Purple</Option>
                <Option value="orange">🟠 Orange</Option>
              </Select>
            </div>
          </div>
          <Divider style={{ margin: '12px 0' }} />
        </>
      )}

      {/* Measurement Status */}
      {measurementMode !== 'idle' && (
        <div className="mb-3">
          <Alert
            message={
              measurementMode === 'first-click' ? 'Click on map to place start point' :
              measurementMode === 'second-click' ? 'Click on map to place end point' :
              'Measurement complete'
            }
            type={measurementMode === 'complete' ? 'success' : 'info'}
            showIcon
            style={{ fontSize: '11px' }}
          />
        </div>
      )}

      {/* Phase 1B: Measurement History */}
      {measurements?.length > 0 && (
        <>
          <div className="mb-3">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong>Measurements ({measurements.length}):</Text>
              <Tooltip title="Clear All Measurements">
                <Button
                  size="small"
                  icon={<ClearOutlined />}
                  onClick={handleClearAllMeasurements}
                  danger
                >
                  Clear All
                </Button>
              </Tooltip>
            </div>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {(() => {
                console.log('🔥 RENDERING MEASUREMENTS LIST (REACTIVE):', measurements);
                return measurements.map(measurement => (
                <div 
                  key={measurement.id}
                  style={{ 
                    padding: '8px',
                    margin: '4px 0',
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        {measurement.label}
                      </div>
                      <div style={{ color: '#666' }}>
                        📏 {measurement.type === 'circle' 
                          ? `${unitPreference === 'mi' ? (measurement.radiusKm * 0.621371).toFixed(2) + ' mi' : measurement.radiusKm + ' km'} radius` 
                          : formatDistanceForUnit(measurement.distance, measurement.distanceKm, measurement.distanceMiles)
                        }
                      </div>
                      <div style={{ color: '#999', fontSize: '10px', marginTop: 2 }}>
                        {new Date(measurement.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Tooltip title={measurement.visible ? "Hide" : "Show"}>
                        <Button
                          size="small"
                          icon={measurement.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                          onClick={() => {
                            console.log('📏 Toggle visibility button clicked, ID:', measurement.id);
                            try {
                              // 🚨 USE DIRECT TOGGLE (SAME AS CIRCLES!)
                              if (map?.measurementFunctionsRef?.current?.toggleMeasurementVisibilityDirect) {
                                console.log('📏 Using DIRECT toggle method (like circles)');
                                map.measurementFunctionsRef.current.toggleMeasurementVisibilityDirect(measurement.id);
                              } else if (map?.measurementFunctionsRef?.current?.toggleMeasurementVisibility) {
                                console.log('📏 Fallback to old toggle method');
                                map.measurementFunctionsRef.current.toggleMeasurementVisibility(measurement.id);
                              } else {
                                console.error('❌ No toggle function available');
                              }
                            } catch (error) {
                              console.error('❌ Exception in toggle:', error);
                            }
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Button
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            console.log('🔥 DELETE BUTTON CLICKED - AGGRESSIVE DEBUG');
                            console.log('🔥 Measurement:', measurement);
                            console.log('🔥 Measurement ID:', measurement.id);
                            console.log('🔥 Measurement Type:', measurement.type);
                            console.log('🔥 Map object:', !!map);
                            console.log('🔥 measurementFunctionsRef:', !!map?.measurementFunctionsRef);
                            console.log('🔥 measurementFunctionsRef.current:', !!map?.measurementFunctionsRef?.current);
                            console.log('🔥 Available functions:', map?.measurementFunctionsRef?.current ? Object.keys(map.measurementFunctionsRef.current) : 'NONE');
                            console.log('🔥 deleteMeasurement function:', typeof map?.measurementFunctionsRef?.current?.deleteMeasurement);
                            
                            try {
                              if (measurement.type === 'circle') {
                                console.log('🔥 CIRCLE DELETION PATH');
                                console.log('🔥 Shapes array:', shapes);
                                console.log('🔥 Looking for measurement ID in shapes:', measurement.id);
                                // The measurement.id should match the shape.id
                                const shapeToDelete = shapes.find(shape => 
                                  shape.type === 'circle' && shape.id === measurement.id
                                );
                                console.log('🔥 Found shape to delete:', shapeToDelete);
                                if (shapeToDelete) {
                                  console.log('🔥 Calling deleteShape...');
                                  deleteShape(shapeToDelete);
                                  console.log('🔥 deleteShape called');
                                } else {
                                  console.log('🔥 FALLBACK: Using deleteMeasurement');
                                  if (map?.measurementFunctionsRef?.current?.deleteMeasurement) {
                                    console.log('🔥 Calling deleteMeasurement fallback...');
                                    map.measurementFunctionsRef.current.deleteMeasurement(measurement.id);
                                    console.log('🔥 deleteMeasurement fallback called');
                                  } else {
                                    console.error('🔥 NO DELETE FUNCTION AVAILABLE!');
                                  }
                                }
                              } else {
                                console.log('🔥 LINE DELETION PATH');
                                if (map?.measurementFunctionsRef?.current?.deleteMeasurement) {
                                  console.log('🔥 Calling deleteMeasurement for line...');
                                  map.measurementFunctionsRef.current.deleteMeasurement(measurement.id);
                                  console.log('🔥 deleteMeasurement for line called');
                                } else {
                                  console.error('🔥 deleteMeasurement function not available');
                                }
                              }
                            } catch (error) {
                              console.error('🔥 EXCEPTION in delete:', error);
                            }
                          }}
                          danger
                        />
                      </Tooltip>
                    </div>
                  </div>
                </div>
                ));
              })()}
            </div>
          </div>
          <Divider style={{ margin: '12px 0' }} />
        </>
      )}

      {/* Drawing Tools */}
      <div className="mb-3">
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Draw Shape:</Text>
        <Space wrap>
          {Object.entries(DRAWING_TOOLS).map(([key, tool]) => (
            <Tooltip key={key} title={tool.title}>
              <Button
                size="small"
                icon={tool.icon}
                type={currentTool === key ? 'primary' : 'default'}
                onClick={() => startDrawing(key)}
                style={currentTool === key ? { backgroundColor: '#39325b', borderColor: '#39325b' } : {}}
              >
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </Button>
            </Tooltip>
          ))}
        </Space>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Shape Management */}
      <div className="mb-3">
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Shape Actions:</Text>
        <Space wrap>
          <Tooltip title="Edit Selected Shape">
            <Button
              size="small"
              icon={<EditOutlined />}
              disabled={!selectedShape || isEditing}
              onClick={() => startEditingShape(selectedShape)}
            >
              {isEditing ? 'Editing...' : 'Edit'}
            </Button>
          </Tooltip>
          
          {isEditing && (
            <Tooltip title="Finish editing and save changes to the shape">
              <Button
                size="small"
                type="primary"
                onClick={stopEditingShape}
              >
                Done Editing
              </Button>
            </Tooltip>
          )}

          <Tooltip title="Delete Selected Shape">
            <Popconfirm
              title="Delete this shape?"
              onConfirm={() => deleteShape()}
              disabled={!selectedShape}
            >
              <Button
                size="small"
                icon={<DeleteOutlined />}
                disabled={!selectedShape}
                danger
              >
                Delete
              </Button>
            </Popconfirm>
          </Tooltip>

          <Tooltip title="Clear All Shapes">
            <Popconfirm
              title="Clear all shapes?"
              onConfirm={clearAllShapes}
              disabled={shapes.length === 0}
            >
              <Button
                size="small"
                icon={<ClearOutlined />}
                disabled={shapes.length === 0}
              >
                Clear All
              </Button>
            </Popconfirm>
          </Tooltip>

          <Tooltip title="Clear Measurement">
            <Button
              size="small"
              icon={<ClearOutlined />}
              disabled={measurementMode === 'idle'}
              onClick={() => {
                console.log('📏 Clear Measure button clicked');
                try {
                  clearMeasurement();
                } catch (error) {
                  console.error('❌ Exception in clearMeasurement:', error);
                }
              }}
              style={{ color: '#ff0000' }}
            >
              Clear Measure
            </Button>
          </Tooltip>
        </Space>
      </div>

      <Divider style={{ margin: '12px 0' }} />



      {/* Active Shapes List */}
      {shapes.length > 0 && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Active Shapes ({shapes.length}):</Text>
            <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
              {shapes.map(shapeData => (
                <div 
                  key={shapeData.id}
                  style={{ 
                    padding: '4px 8px',
                    margin: '2px 0',
                    backgroundColor: selectedShape?.id === shapeData.id ? '#f0f0f0' : 'transparent',
                    border: selectedShape?.id === shapeData.id ? '1px solid #39325b' : '1px solid transparent',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                  onClick={() => selectShape(shapeData)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                      <span style={{ color: '#39325b' }}>●</span>
                      {' '}{shapeData.type.charAt(0).toUpperCase() + shapeData.type.slice(1)}
                      {shapeData.type === 'circle' && (
                        <span style={{ marginLeft: 4, fontSize: '10px', color: '#666' }}>📏</span>
                      )}
                    </span>
                    <Button
                      size="small"
                      icon={shapeData.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleShapeVisibility(shapeData);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Instructions */}
      <Alert
        message="Drawing Tips"
        description="Select a mode and draw shapes. Double-click shapes to edit. Use different selection modes to combine/subtract selections."
        type="info"
        showIcon
        style={{ marginTop: 12, fontSize: '11px' }}
      />
        </Panel>
      </Collapse>
      <MobileConsole logs={mobileLogs} />
    </div>
  );
};

export default AdvancedDrawingTools; 